import { diffDays } from '../../shared/date'
import type { Prediction } from '../../shared/predict'
import type { ReactNode } from 'react'

/**
 * 周期圆环。一圈就是一个完整周期，12 点方向是周期第一天，顺时针推进。
 *
 * 环上叠了四层信息：
 *   · 粉色实心段 = 已记录的经期
 *   · 粉色半透明段 = 预测经期的可能区间（宽窄直接反映预测有多确定）
 *   · 绿色段 = 易孕窗，中间那个深色圆点是预计排卵日
 *   · 白边圆点 = 今天在周期里的位置
 *
 * 刻意不把预测经期画成某一天的实心块 —— 预测本来就有误差，
 * 画成一段渐变的区间才是诚实的表达。
 */

const SIZE = 220
const C = SIZE / 2
const R = 92
const STROKE = 16

function polar(r: number, deg: number) {
  const a = ((deg - 90) * Math.PI) / 180
  return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) }
}

function arc(r: number, fromDeg: number, toDeg: number): string {
  const sweep = toDeg - fromDeg
  // 整圈用两段半圆拼，单条 A 命令画不出 360°
  if (sweep >= 359.9) {
    const a = polar(r, 0)
    const b = polar(r, 180)
    return `M ${a.x} ${a.y} A ${r} ${r} 0 1 1 ${b.x} ${b.y} A ${r} ${r} 0 1 1 ${a.x} ${a.y}`
  }
  const s = polar(r, fromDeg)
  const e = polar(r, toDeg)
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${sweep > 180 ? 1 : 0} 1 ${e.x} ${e.y}`
}

export function CycleRing({
  prediction,
  periodDays,
  children,
}: {
  prediction: Prediction
  /** 本周期内已经记录了流量的日子 */
  periodDays: string[]
  children: ReactNode
}) {
  const { currentCycleStart, cycleLength, nextPeriod, ovulation, fertileWindow } = prediction

  if (!currentCycleStart || !nextPeriod) {
    return (
      <RingShell center={children}>
        <circle
          cx={C}
          cy={C}
          r={R}
          fill="none"
          stroke="var(--c-bg-soft)"
          strokeWidth={STROKE}
        />
      </RingShell>
    )
  }

  const offset = (d: string) => diffDays(currentCycleStart, d)
  const cycleDay = prediction.cycleDay ?? 1

  // 圈的长度取「预测周期长度」和「今天/预测区间实际到了第几天」的较大值，
  // 这样经期推迟时环会自动变长，今天的标记不会跑到圈外面
  const span = Math.max(
    Math.round(cycleLength.mean),
    cycleDay,
    offset(nextPeriod.latest) + 1,
  )
  const deg = (dayOffset: number) => (dayOffset / span) * 360

  // 已记录的经期：合并成连续段，避免每天画一条产生缝隙
  const loggedOffsets = periodDays
    .map(offset)
    .filter((o) => o >= 0 && o < span)
    .sort((a, b) => a - b)
  const loggedRuns: [number, number][] = []
  for (const o of loggedOffsets) {
    const last = loggedRuns[loggedRuns.length - 1]
    if (last && o === last[1] + 1) last[1] = o
    else loggedRuns.push([o, o])
  }

  const fertileFrom = fertileWindow ? Math.max(0, offset(fertileWindow.start)) : 0
  const fertileTo = fertileWindow ? Math.min(span, offset(fertileWindow.end) + 1) : 0
  const showFertile = Boolean(fertileWindow) && fertileTo > fertileFrom

  const predFrom = Math.max(0, offset(nextPeriod.earliest))
  const predTo = Math.min(span, offset(nextPeriod.latest) + 1)

  const todayPoint = polar(R, deg(cycleDay - 1) + deg(0.5))
  const ovPoint = ovulation ? polar(R, deg(offset(ovulation.expected) + 0.5)) : null

  return (
    <RingShell center={children}>
      {/* 底环 */}
      <circle cx={C} cy={C} r={R} fill="none" stroke="var(--c-bg-soft)" strokeWidth={STROKE} />

      {/* 预测经期区间：越宽说明越不确定 */}
      {predTo > predFrom && (
        <path
          d={arc(R, deg(predFrom), deg(predTo))}
          fill="none"
          stroke="var(--c-period)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          opacity={0.28}
        />
      )}

      {/* 易孕窗 */}
      {showFertile && (
        <path
          d={arc(R, deg(fertileFrom), deg(fertileTo))}
          fill="none"
          stroke="var(--c-fertile)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          opacity={0.42}
        />
      )}

      {/* 已记录的经期 */}
      {loggedRuns.map(([from, to]) => (
        <path
          key={from}
          d={arc(R, deg(from), deg(to + 1))}
          fill="none"
          stroke="var(--c-period)"
          strokeWidth={STROKE}
          strokeLinecap="round"
        />
      ))}

      {/* 预计排卵日 */}
      {ovPoint && (
        <circle cx={ovPoint.x} cy={ovPoint.y} r={4.5} fill="var(--c-ovulation)" />
      )}

      {/* 今天 */}
      <circle
        cx={todayPoint.x}
        cy={todayPoint.y}
        r={7.5}
        fill="var(--c-card)"
        stroke="var(--c-text)"
        strokeWidth={2.5}
      />
    </RingShell>
  )
}

/**
 * 环本身画在 SVG 里，中间的文字用普通 HTML 叠上去 ——
 * SVG 里的文字读屏软件读不利索，而这段恰恰是最需要被读出来的信息。
 */
function RingShell({ children, center }: { children: ReactNode; center: ReactNode }) {
  return (
    <div className="relative mx-auto" style={{ width: SIZE, height: SIZE }}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="absolute inset-0"
        aria-hidden
      >
        {children}
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center px-9">
        <div>{center}</div>
      </div>
    </div>
  )
}
